import React from 'react';
import { Box, Button, Container, Typography } from '@mui/material';
import { brand } from '../thema';

export type Tone = 'light' | 'alt' | 'dark';

const backgroundByTone: Record<Tone, string> = {
    light: brand.paper,
    alt: brand.paperAlt,
    dark: brand.dark,
};

interface SectionProps {
    id?: string;
    tone?: Tone;
    grid?: boolean;
    children: React.ReactNode;
}

// トップページの各セクションの外枠
export const Section: React.FC<SectionProps> = ({ id, tone = 'light', grid = false, children }) => (
    <Box
        component="section"
        id={id}
        sx={{
            backgroundColor: backgroundByTone[tone],
            color: tone === 'dark' ? '#ffffff' : brand.ink,
            py: { xs: 10, md: 16 },
            scrollMarginTop: '64px',
            ...(grid && {
                backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.035) 1px, transparent 1px)',
                backgroundSize: '56px 56px',
            }),
        }}
    >
        <Container maxWidth="lg" sx={{ px: { xs: 3, md: 4 } }}>
            {children}
        </Container>
    </Box>
);

interface EyebrowProps {
    index?: string;
    label: string;
    tone?: Tone;
}

// 「01 / PRICING」形式のセクションラベル
export const Eyebrow: React.FC<EyebrowProps> = ({ index, label, tone = 'light' }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            fontFamily: brand.fontMono,
            fontSize: '0.75rem',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: tone === 'dark' ? brand.accent : brand.accentText,
        }}
    >
        <Box component="span" sx={{ width: 24, height: '1px', backgroundColor: 'currentColor' }} />
        <span>{index ? `${index} / ${label}` : label}</span>
    </Box>
);

interface SectionHeadingProps {
    index?: string;
    eyebrow: string;
    title: string;
    lead?: string;
    tone?: Tone;
}

export const SectionHeading: React.FC<SectionHeadingProps> = ({ index, eyebrow, title, lead, tone = 'light' }) => (
    <Box sx={{ mb: { xs: 6, md: 8 } }}>
        <Eyebrow index={index} label={eyebrow} tone={tone} />
        <Typography
            component="h2"
            sx={{
                mt: 2.5,
                fontSize: { xs: '1.5rem', md: '2.25rem' },
                fontWeight: 700,
                lineHeight: 1.5,
                letterSpacing: '0.03em',
                whiteSpace: 'pre-line',
            }}
        >
            {title}
        </Typography>
        {lead && <Lead tone={tone}>{lead}</Lead>}
    </Box>
);

interface LeadProps {
    tone?: Tone;
    children: React.ReactNode;
}

// 本文リード。改行はPC幅のみ反映し、スマホでは自然に折り返す
export const Lead: React.FC<LeadProps> = ({ tone = 'light', children }) => (
    <Typography
        sx={{
            mt: 3,
            maxWidth: 760,
            fontSize: { xs: '0.9375rem', md: '1rem' },
            lineHeight: 2,
            color: tone === 'dark' ? 'rgba(255, 255, 255, 0.78)' : brand.inkSub,
            whiteSpace: { xs: 'normal', md: 'pre-line' },
        }}
    >
        {children}
    </Typography>
);

interface MarkerListProps {
    items: string[];
    tone?: Tone;
    variant?: 'check' | 'dash';
    columns?: { xs?: number; sm?: number; md?: number };
}

export const MarkerList: React.FC<MarkerListProps> = ({ items, tone = 'light', variant = 'check', columns = { xs: 1 } }) => (
    <Box
        component="ul"
        sx={{
            listStyle: 'none',
            p: 0,
            m: 0,
            display: 'grid',
            gridTemplateColumns: {
                xs: `repeat(${columns.xs ?? 1}, minmax(0, 1fr))`,
                sm: `repeat(${columns.sm ?? columns.xs ?? 1}, minmax(0, 1fr))`,
                md: `repeat(${columns.md ?? columns.sm ?? columns.xs ?? 1}, minmax(0, 1fr))`,
            },
            columnGap: 4,
            rowGap: 1.5,
        }}
    >
        {items.map((item) => (
            <Box
                component="li"
                key={item}
                sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 1.5,
                    fontSize: '0.9375rem',
                    lineHeight: 1.8,
                    color: tone === 'dark' ? 'rgba(255, 255, 255, 0.86)' : brand.ink,
                }}
            >
                <Box
                    component="span"
                    aria-hidden
                    sx={{
                        flexShrink: 0,
                        width: variant === 'check' ? 6 : 8,
                        height: variant === 'check' ? 6 : '1px',
                        backgroundColor: variant === 'check' ? brand.accent : brand.muted,
                        transform: 'translateY(-3px)',
                    }}
                />
                <span>{item}</span>
            </Box>
        ))}
    </Box>
);

interface CtaButtonProps {
    href?: string;
    onClick?: () => void;
    tone?: Tone;
    variant?: 'primary' | 'secondary';
    children: React.ReactNode;
}

export const CtaButton: React.FC<CtaButtonProps> = ({ href, onClick, tone = 'light', variant = 'primary', children }) => {
    const onDark = tone === 'dark';
    const primary = variant === 'primary';
    return (
        <Button
            href={href}
            onClick={onClick}
            disableElevation
            variant={primary ? 'contained' : 'outlined'}
            sx={{
                borderRadius: 0,
                px: 4,
                py: 1.75,
                fontSize: '0.9375rem',
                fontWeight: 500,
                letterSpacing: '0.06em',
                textTransform: 'none',
                ...(primary
                    ? {
                        backgroundColor: onDark ? '#ffffff' : brand.ink,
                        color: onDark ? brand.ink : '#ffffff',
                        '&:hover': { backgroundColor: onDark ? '#E6E8EA' : '#2A2F36' },
                    }
                    : {
                        color: onDark ? '#ffffff' : brand.ink,
                        borderColor: onDark ? 'rgba(255, 255, 255, 0.5)' : brand.ink,
                        '&:hover': {
                            borderColor: onDark ? '#ffffff' : brand.ink,
                            backgroundColor: onDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(17, 20, 24, 0.04)',
                        },
                    }),
            }}
        >
            {children}
        </Button>
    );
};

// 等幅フォントの小見出し・番号
export const Mono: React.FC<{ children: React.ReactNode; color?: string; size?: string }> = ({ children, color, size = '0.75rem' }) => (
    <Box component="span" sx={{ fontFamily: brand.fontMono, fontSize: size, letterSpacing: '0.08em', color: color ?? brand.muted }}>
        {children}
    </Box>
);
